<template>
  <dialog
    class="JoinRunModalView"
    data-theme="light"
    aria-label="Request to join the run"
    :aria-busy="isFetchingRunInvite"
    open
  >
    <div
      class="JoinRunModalView__backdrop"
      data-testid="backdrop"
      @click="closeActiveModal()"
    />
    <div
      class="JoinRunModalView__windowContainer"
    >
      <section
        class="JoinRunModalView__window"
      >
        <header class="JoinRunModalView__header">
          <figure class="JoinRunModalView__avatarContainer">
            <PlayerAvatar
              :modelValue="invitingPlayer || null"
            />
            <Icon
              name="plus"
              :style="`--iconSize: 20px; color: var(--themeColorNeutral300)`"
            />
            <PlayerAvatar
              :modelValue="currentPlayer || null"
            />
          </figure>
          <h1 class="JoinRunModalView__title">
            <template v-if="invitingPlayer">
              {{ invitingPlayer.name }} invites you to hoop
            </template>
            <template v-else>
              …
            </template>
          </h1>
          <h2 class="JoinRunModalView__meta">
            <template v-if="!isFetchingRunInvite">
              <time :datetime="run?.start_at">
                {{ formattedRelativeDay }}, {{ formattedShortDate }}
                @
                {{ formattedStartTime }}<template v-if="run?.end_at && formattedEndTime"> – {{ formattedEndTime }}</template>
                <span
                  v-if="run?.court"
                >
                  &nbsp;at {{ run.court.name }}
                </span>
              </time>
            </template>
            <template v-else>
              …
            </template>
          </h2>
        </header>
        <FormButton
          :disabled="isFetchingRunInvite || isRequestingToJoin"
          @click="handleRequestClick"
        >
          <span v-if="isFetchingRunInvite">
            Pulling up your invite…
          </span>
          <span v-else-if="isRequestingToJoin">
            Requesting to join…
          </span>
          <span v-else>
            Request to join
          </span>
        </FormButton>
      </section>
    </div>
  </dialog>
</template>

<script lang="ts" setup>
import FormButton from "@/components/FormButton.vue";
import Icon from "@/components/Icon.vue";
import PlayerAvatar from "@/components/PlayerAvatar.vue";
import useRunSchedule from "@/composables/useRunSchedule";
import { supabaseClient } from "@/lib/database/supabase";
import getRouteForRun from "@/lib/utils/route/getRouteForRun";
import useAuthStore from "@/stores/useAuthStore";
import useModalStore from "@/stores/useModalStore";
import { PublicRunInvite } from "@shared/lib/database/query.types";
import { storeToRefs } from "pinia";
import { computed, onBeforeMount, ref } from "vue";
import { useRouter } from "vue-router";

const {
  runInviteId,
} = defineProps<{
  runInviteId: string,
}>();

const router = useRouter();

const {
  currentPlayer,
} = storeToRefs(useAuthStore());

const {
  closeActiveModal,
} = useModalStore();

const isFetchingRunInvite = ref(true);
const isRequestingToJoin = ref(false);

const runInvite = ref<PublicRunInvite>();

const run = computed(() => {
  if (!runInvite.value) return null;

  return {
    id: runInvite.value.run_id,
    start_at: runInvite.value.run_start_at,
    end_at: runInvite.value.run_end_at || null,
    court: runInvite.value.court_name
      ? {
        name: runInvite.value.court_name,
        timezone: runInvite.value.court_timezone,
      }
      : null,
  };
});

const invitingPlayer = computed(() => {
  if (!runInvite.value) return null;

  return {
    id: runInvite.value.player_id,
    name: runInvite.value.player_name,
    email: runInvite.value.player_email,
    theme: runInvite.value.player_theme,
    avatar_storage_path: null,
  };
});

const {
  formattedShortDate,
  formattedRelativeDay,
  formattedStartTime,
  formattedEndTime,
} = useRunSchedule(run);

onBeforeMount(async () => {
  isFetchingRunInvite.value = true;

  const {
    data: _runInvite,
    error,
  } = await supabaseClient
    .rpc("get_public_run_invite", {
      run_invite_id: runInviteId,
    })
    .single();

  if (_runInvite && _runInvite.player_id === currentPlayer.value?.id) {
    closeActiveModal();

    router.push(getRouteForRun(_runInvite.run_id));
  } else if (_runInvite) {
    runInvite.value = _runInvite;
  } else if (error) {
    alert(error.message);
  }

  isFetchingRunInvite.value = false;
});

async function handleRequestClick () {
  if (!currentPlayer.value || !runInvite.value) return;

  isRequestingToJoin.value = true;
  const {
    error,
  } = await supabaseClient.from("sub_requests")
    .upsert({
      run_id: runInvite.value.run_id,
      player_id: currentPlayer.value.id,
      probability: 1,
      run_invite_id: runInvite.value.id,
    }, {
      onConflict: "run_id,player_id",
      ignoreDuplicates: true,
    });

  if (!error) {
    closeActiveModal();

    router.push(getRouteForRun(runInvite.value.run_id));
  } else {
    isRequestingToJoin.value = false;

    alert(error.message);
  }
}
</script>

<style lang="scss" scoped>
.JoinRunModalView {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 999;
  isolation: isolate;

  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  padding: 24px 16px;

  transition: none 350ms;
}

.JoinRunModalView__backdrop {
  position: absolute;
  inset: 0;
  z-index: -1;

  background-color: var(--themeColorBackdrop);
  backdrop-filter: blur(6px);

  cursor: pointer;

  transition: opacity 350ms var(--easeOutBack);

  .v-leave-active & {
    transition: 350ms;
    transition-timing-function: initial;
  }

  .v-enter-from &,
  .v-leave-to & {
    opacity: 0;
  }
}

.JoinRunModalView__windowContainer {
  width: 100%;
}

.JoinRunModalView__window {
  display: grid;
  gap: 24px;
  padding: 28px 16px 16px;

  border-radius: 16px;
  background-color: var(--themeColorNeutral0);

  text-align: center;

  .v-enter-active & {
    transition: 335ms cubic-bezier(0.25, 1, 0.30, 1);
    transition-property: opacity, transform;
  }

  .v-leave-active & {
    transition: 350ms;
    transition-property: opacity;
  }

  .v-enter-from & {
    transform: scale(0.8);
    opacity: 0;
  }

  .v-leave-to & {
    opacity: 0;
  }
}

.JoinRunModalView__header {
  display: grid;
}

.JoinRunModalView__avatarContainer {
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
}

.JoinRunModalView__title {
  font: var(--fontWeightMedium) 24px / 1.2 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);
}

.JoinRunModalView__meta {
  margin-top: 8px;

  color: var(--themeColorNeutral700);

  font: var(--fontWeightNormal) 18px / 1.5 var(--fontFamilyNormal);
  font-stretch: var(--fontWidthExtended);

  span {
    display: inline-block;
    white-space: nowrap;
  }
}
</style>
